import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  Account,
  AccountListAggregate,
  AccountsRequestContext,
  AccountsSetValues,
} from '@helios/generic-components'
import {
  FilterType,
  genericHeliosRequest,
  HeliosServerSideRequest,
  NumberFilter,
} from '@helios/server-side-util'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { forkJoin, Observable, of } from 'rxjs'
import { map, take } from 'rxjs/operators'
import {
  AccountListService,
  AccountOwnerField,
  AdvancedFilterContext,
  ExportExcelRequest,
  ReconAIRequest,
} from './account-list.service'

/** @internal */
export const ACCOUNT_LIST_URL = `helios-accounts`

/** @internal */
export const ACCOUNTS_SET_VALUES_URL = `${ACCOUNT_LIST_URL}/set-values`

/** @internal */
export const ACCOUNTS_AGGREGATES = `${ACCOUNT_LIST_URL}`

/** @internal */
export interface GetAccountsResponse {
  data: Account[]
}

@Injectable({
  providedIn: 'root',
})
export class HttpAccountListService extends AccountListService {
  constructor(private readonly http: HttpClient) {
    super()
  }

  /**
   * Takes a helios request and returns account list row data.
   *
   * @param request - agGrid row data request that has been normalized into a helios request.
   * @returns Account list row data - The observable will emit two values. The first value will have the account list
   * row data without the usersInAccount data point. The second time the observable emits the same account list data
   * will be emitted except it will have the the usersInAccount data point.
   */
  getAccounts(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<Account[]> {
    return this.http
      .post<{ data: Account[] }>(ACCOUNT_LIST_URL, request)
      .pipe(map(response => response.data ?? []))
  }

  /**
   * Fetches ag-grid filter set values for the account list.
   *
   * @returns ag-grid filter set values. For example the backend might tell us that for accountType we can only filter
   * these options: 'simple' | 'aggregate'.
   */
  getAccountsSetValues(): Observable<AccountsSetValues> {
    return this.http.get<AccountsSetValues>(ACCOUNTS_SET_VALUES_URL)
  }

  /**
   * Fetches aggregates for a specific account id for the account list
   */
  getAggregates(accountId: number): Observable<AccountListAggregate[]> {
    return this.http.get<AccountListAggregate[]>(
      `${ACCOUNTS_AGGREGATES}/${accountId}/aggregates`,
    )
  }

  getAccountById(
    accountId: number,
    fields: (keyof Account)[],
    advancedFilterContext: AdvancedFilterContext,
  ): Observable<Account[]> {
    return this.getAccounts(
      this.getHeliosRequestFromAccountId(
        accountId,
        fields,
        advancedFilterContext,
      ),
    ).pipe(take(1))
  }

  private getHeliosRequestFromAccountId(
    accountId: number,
    fields: (keyof Account)[],
    advancedFilterContext: AdvancedFilterContext,
  ): HeliosServerSideRequest<Account, AccountsRequestContext> {
    return {
      ...genericHeliosRequest,
      endRow: 1,
      fields,
      filter: {
        accountId: {
          operator: 'AND',
          filterType: 'number',
          filters: [
            {
              operator: 'equals',
              value: accountId,
            },
          ],
        } as NumberFilter,
      },
      context: {
        filterContext: { ...advancedFilterContext },
      } as AccountsRequestContext,
    }
  }

  getAccountIds(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<number[]> {
    return this.http
      .post<GetAccountsResponse>(
        ACCOUNT_LIST_URL,
        this.getHeliosRequestForOnlyAccountIds(request),
      )
      .pipe(
        map(response => response.data?.map(account => account.accountId) ?? []),
      )
  }

  private getHeliosRequestForOnlyAccountIds(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): HeliosServerSideRequest<Account, AccountsRequestContext> {
    return {
      ...request,
      startRow: 0,
      endRow: 10001, // filter action menu supports up to 10000 accounts
      fields: ['accountId'],
    }
  }

  getAccountsExportExcel(request: ExportExcelRequest): Observable<Blob> {
    const options = {
      responseType: 'blob' as const,
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    const requestPayload = {
      ...genericHeliosRequest,
      headers: request.headers,
      fields: request.fields,
      context: request.context,
      filter: request.filters,
      userTimeZone: request.userTimeZone,
    }

    return this.http.post(`${ACCOUNT_LIST_URL}/export`, requestPayload, options)
  }

  runAIForAccount(request: ReconAIRequest): Observable<string> {
    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Accepts: 'application/json',
      }),
      context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
    }
    const requestPayload = {
      accountId: request.accountId,
      reconDate: request.reconDate,
    }

    return this.http.post<string>(
      `${ACCOUNT_LIST_URL}/runAIForAccount`,
      requestPayload,
      options,
    )
  }

  getAccountIdsForFilterAction(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
    filterCount: number,
  ): Observable<GetAccountsResponse[]> {
    const requests = this.getHeliosRequestBatchsForActionMenu(
      request,
      filterCount,
    )
    const accountIdRequests = requests.map(req => {
      return this.http.post<GetAccountsResponse>(ACCOUNT_LIST_URL, req)
    })
    return forkJoin(accountIdRequests)
  }

  private getHeliosRequestBatchsForActionMenu(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
    filterCount: number,
  ): HeliosServerSideRequest<Account, AccountsRequestContext>[] {
    const result = []
    const totalRows = filterCount
    const rowIncrement = 2000

    for (let startRow = 0; startRow < totalRows; startRow += rowIncrement) {
      const endRow = Math.min(startRow + rowIncrement, totalRows)

      result.push({
        ...request,
        startRow: startRow,
        endRow: endRow,
        fields: ['accountId'],
      })
    }
    return result
  }

  getNeedsOmegaTsCount(
    reconDate: Date,
    userNames: string[],
    field: AccountOwnerField,
  ): Observable<number> {
    if (userNames.length === 0) {
      return of(0)
    }
    const request: HeliosServerSideRequest<Account, AccountsRequestContext> = {
      ...genericHeliosRequest,
      filter: {
        [field]: {
          filterType: FilterType.Set,
          values: userNames,
        },
      },
      context: {
        filterContext: {
          reconDate,
          advancedFilters: {
            hasOmegaDate: false,
          },
        },
      },
    }

    return this.http
      .post<{ rowCount: number }>(`${ACCOUNT_LIST_URL}/summaries`, request)
      .pipe(map(response => response.rowCount))
  }

  getAccountListCountForFilter(
    request: HeliosServerSideRequest<Account, AccountsRequestContext>,
  ): Observable<number> {
    return this.http
      .post<{ rowCount: number }>(`${ACCOUNT_LIST_URL}/summaries`, request)
      .pipe(map(response => response.rowCount))
  }

  refreshAccount(accountId: number): Promise<void> {
    return this.http
      .put<void>(`${ACCOUNT_LIST_URL}/${accountId}/refresh`, null)
      .pipe(take(1))
      .toPromise()
  }
}
